import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { enqueueOp, isOffline, isNetworkError, type CreditTxPayload } from '@/lib/offlineQueue';
import { computeBalance,
  groupByCustomer,
  generateToken,
  generateUsername,
  generatePassword,
  DEFAULT_REMINDER_PREFS,
  type CreditCustomer,
  type CreditReminder,
  type CreditReminderPrefs,
  type CreditSettings,
  type CreditTransaction,
  type CreditTxType,
  type CreditImportBatch,
} from '@/lib/credit';
import { getOrganizationByCommerce, linkCreditCustomerToCore } from '@/lib/zifekCore';
import { logAudit } from '@/lib/coreAudit';

export interface NewTransactionInput {
  customerId: number;
  type: CreditTxType;
  amount: number;
  description?: string;
  paymentMethod?: string;
  reversesTransactionId?: number | null;
  /** Photo de preuve (produits pris à crédit, reçu…). */
  proofUrl?: string | null;
}

export interface CreditStats {
  totalOutstanding: number;
  customersWithCredit: number;
  creditsThisMonth: number;
  paymentsThisMonth: number;
  needingAttention: number;
  totalDeposits: number;
}

const emptyStats: CreditStats = {
  totalOutstanding: 0,
  customersWithCredit: 0,
  creditsThisMonth: 0,
  paymentsThisMonth: 0,
  needingAttention: 0,
  totalDeposits: 0,
};

function isSameMonth(iso: string, ref: Date): boolean {
  const d = new Date(iso);
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth();
}

export function useCredit() {
  const { user } = useAuth();
  const merchantId = user?.id ?? null;

  const [customers, setCustomers] = useState<CreditCustomer[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [settings, setSettings] = useState<CreditSettings | null>(null);
  const [reminders, setReminders] = useState<CreditReminder[]>([]);
  const [importBatches, setImportBatches] = useState<CreditImportBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currency = settings?.currency || user?.monaie || 'MAD';

  const load = useCallback(async (silent = false) => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    // `silent === true` : rafraîchissement discret (pas de spinner plein écran),
    // utile après une action lancée depuis une modale (points, transfert, dépôt...).
    if (silent !== true) setLoading(true);
    setError(null);
    try {
      const [customersRes, txRes, settingsRes, remindersRes, batchesRes] = await Promise.all([
        supabase
          .from('credit_customers')
          .select('*')
          .eq('merchant_id', merchantId)
          .order('full_name', { ascending: true }),
        supabase
          .from('credit_transactions')
          .select('*')
          .eq('merchant_id', merchantId)
          .order('created_at', { ascending: false }),
        supabase.from('credit_settings').select('*').eq('merchant_id', merchantId).maybeSingle(),
        supabase
          .from('credit_reminders')
          .select('*')
          .eq('merchant_id', merchantId)
          .order('created_at', { ascending: false })
          .limit(30),
        supabase
          .from('credit_import_batches')
          .select('*')
          .eq('merchant_id', merchantId)
          .order('created_at', { ascending: false })
          .limit(50),
      ]);

      if (customersRes.error) throw customersRes.error;
      if (txRes.error) throw txRes.error;

      setCustomers((customersRes.data as CreditCustomer[]) || []);
      setTransactions((txRes.data as CreditTransaction[]) || []);
      setSettings((settingsRes.data as CreditSettings) || null);
      setReminders((remindersRes.data as CreditReminder[]) || []);
      setImportBatches((batchesRes.data as CreditImportBatch[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement du carnet de crédit');
    } finally {
      if (silent !== true) setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  // Recharge le carnet après une synchronisation hors-ligne réussie.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('zifek-synced', handler);
    return () => window.removeEventListener('zifek-synced', handler);
  }, [load]);

  const byCustomer = useMemo(() => groupByCustomer(transactions), [transactions]);

  const balances = useMemo(() => {
    const map: Record<number, number> = {};
    customers.forEach((c) => {
      map[c.id] = computeBalance(byCustomer[c.id] || []);
    });
    return map;
  }, [customers, byCustomer]);

  const stats = useMemo<CreditStats>(() => {
    if (customers.length === 0) return emptyStats;
    const now = new Date();
    let totalOutstanding = 0;
    let customersWithCredit = 0;
    let needingAttention = 0;
    let totalDeposits = 0;
    customers.forEach((c) => {
      const bal = balances[c.id] || 0;
      if (bal > 0) {
        totalOutstanding += bal;
        customersWithCredit += 1;
        if (c.credit_limit != null && c.credit_limit > 0 && bal >= c.credit_limit) {
          needingAttention += 1;
        }
      } else if (bal < 0) {
        // Solde négatif = fonds avancés par le client, détenus chez le vendeur.
        totalDeposits += -bal;
      }
    });
    let creditsThisMonth = 0;
    let paymentsThisMonth = 0;
    transactions.forEach((tx) => {
      if (!isSameMonth(tx.created_at, now)) return;
      if (tx.type === 'credit') creditsThisMonth += Number(tx.amount) || 0;
      if (tx.type === 'payment') paymentsThisMonth += Number(tx.amount) || 0;
    });
    return { totalOutstanding, customersWithCredit, creditsThisMonth, paymentsThisMonth, needingAttention, totalDeposits };
  }, [customers, transactions, balances]);

  const transactionsFor = useCallback(
    (customerId: number): CreditTransaction[] => byCustomer[customerId] || [],
    [byCustomer]
  );

  const balanceFor = useCallback((customerId: number): number => balances[customerId] || 0, [balances]);

  // ── Clients ────────────────────────────────────────────────
  const createCustomer = useCallback(
    async (data: Partial<CreditCustomer>): Promise<CreditCustomer> => {
      if (!merchantId) throw new Error('Non connecté');
      // Un client créé manuellement reçoit automatiquement un identifiant
      // + un mot de passe par défaut, que le commerçant peut partager.
      const username = data.username || generateUsername(data.full_name || 'client');
      const tempPassword = data.temp_password || generatePassword();
      const { data: inserted, error: insertError } = await supabase
        .from('credit_customers')
        .insert({
          merchant_id: merchantId,
          full_name: data.full_name,
          phone: data.phone || null,
          credit_limit: data.credit_limit ?? null,
          usual_payment_date: data.usual_payment_date || null,
          notes: data.notes || null,
          email: (data as { email?: string }).email || null,
          username,
          temp_password: tempPassword,
          status: 'active',
        })
        .select('*')
        .single();
      if (insertError) throw insertError;
      const customer = inserted as CreditCustomer;
      setCustomers((prev) => [...prev, customer].sort((a, b) => a.full_name.localeCompare(b.full_name)));
      // Fiche client unique : relie ce client du carnet à son organisation (best-effort).
      if (merchantId) {
        void linkCreditCustomerToCore({
          id: customer.id,
          merchant_id: merchantId,
          full_name: customer.full_name,
          phone: customer.phone,
          email: (customer as { email?: string | null }).email ?? null,
          linked_user_id: customer.linked_user_id,
        });
      }
      return customer;
    },
    [merchantId]
  );

  const updateCustomer = useCallback(async (id: number, data: Partial<CreditCustomer>) => {
    const { error: updateError } = await supabase
      .from('credit_customers')
      .update({
        full_name: data.full_name,
        phone: data.phone || null,
        credit_limit: data.credit_limit ?? null,
        usual_payment_date: data.usual_payment_date || null,
        notes: data.notes || null,
        email: (data as { email?: string }).email ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);
    if (updateError) throw updateError;
    setCustomers((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    // Re-synchronise le lien vers la fiche client unique (nom / email / tél. modifiés).
    if (merchantId) {
      const base: CreditCustomer = customers.find((c) => c.id === id) ?? ( {} as CreditCustomer);
      const updated: CreditCustomer = { ...base, ...data };
      void linkCreditCustomerToCore({
        id,
        merchant_id: merchantId,
        full_name: updated.full_name || '',
        phone: updated.phone ?? null,
        email: (updated as { email?: string | null }).email ?? null,
        linked_user_id: updated.linked_user_id ?? null,
      });
    }
  }, [merchantId, customers]);

  // Régénère l'identifiant + le mot de passe par défaut d'un client.
  const regenerateCredentials = useCallback(
    async (id: number): Promise<{ username: string; password: string }> => {
      const target = customers.find((c) => c.id === id);
      const username = generateUsername(target?.full_name || 'client');
      const password = generatePassword();
      const { error: updError } = await supabase
        .from('credit_customers')
        .update({ username, temp_password: password, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (updError) throw updError;
      setCustomers((prev) => prev.map((c) => (c.id === id ? { ...c, username, temp_password: password } : c)));
      return { username, password };
    },
    [customers]
  );

  const deleteCustomer = useCallback(async (id: number) => {
    const target = customers.find((c) => c.id === id);
    const { error: delError } = await supabase.from('credit_customers').delete().eq('id', id);
    if (delError) throw delError;
    await supabase.from('credit_transactions').delete().eq('customer_id', id);
    setCustomers((prev) => prev.filter((c) => c.id !== id));
    setTransactions((prev) => prev.filter((tx) => tx.customer_id !== id));
    // Journalise la suppression (auteur + horodatage) — best-effort.
    try {
      if (merchantId) {
        const org = await getOrganizationByCommerce(merchantId);
        if (org) {
          await logAudit({
            tenantId: org.tenant_id, organizationId: org.id,
            userId: user?.id ?? null, userName: user?.name ?? null,
            action: 'delete', resourceType: 'customer', resourceId: String(id),
            before: { full_name: target?.full_name ?? null, phone: target?.phone ?? null },
          });
        }
      }
    } catch { /* best-effort */ }
  }, [merchantId, customers, user]);

  // ── Transactions (immuables) ───────────────────────────────
  const addTransaction = useCallback(
    async (input: NewTransactionInput): Promise<CreditTransaction> => {
      if (!merchantId) throw new Error('Non connecté');

      const payload: CreditTxPayload = {
        customerId: input.customerId,
        type: input.type,
        amount: input.amount,
        description: input.description || null,
        paymentMethod: input.paymentMethod || null,
        reversesTransactionId: input.reversesTransactionId ?? null,
      };

      // Transaction locale optimiste (id temporaire négatif) affichée
      // immédiatement, remplacée par la vraie valeur après synchronisation.
      const buildOptimistic = (): CreditTransaction => ({
        id: -Date.now(),
        merchant_id: merchantId,
        customer_id: input.customerId,
        type: input.type,
        amount: input.amount,
        description: input.description || null,
        payment_method: input.paymentMethod || null,
        created_by: merchantId,
        reverses_transaction_id: input.reversesTransactionId ?? null,
        created_at: new Date().toISOString(),
      });

      const queueOffline = async (): Promise<CreditTransaction> => {
        await enqueueOp('credit_tx', payload);
        const optimistic = buildOptimistic();
        setTransactions((prev) => [optimistic, ...prev]);
        return optimistic;
      };

      // Hors ligne : on met en file et on continue à travailler localement.
      if (isOffline()) return queueOffline();

      try {
        const baseRow = {
          merchant_id: merchantId,
          customer_id: input.customerId,
          type: input.type,
          amount: input.amount,
          description: input.description || null,
          payment_method: input.paymentMethod || null,
          created_by: merchantId,
          reverses_transaction_id: input.reversesTransactionId ?? null,
        };

        let inserted: unknown = null;
        let insertError: unknown = null;
        {
          const first = await supabase
            .from('credit_transactions')
            .insert(input.proofUrl ? { ...baseRow, proof_url: input.proofUrl } : baseRow)
            .select('*')
            .single();
          inserted = first.data;
          insertError = first.error;
        }
        // Si la colonne « preuve photo » n'est pas encore disponible, on
        // enregistre la transaction sans la photo plutôt que d'échouer.
        if (insertError && input.proofUrl) {
          const retry = await supabase
            .from('credit_transactions')
            .insert(baseRow)
            .select('*')
            .single();
          inserted = retry.data;
          insertError = retry.error;
        }
        if (insertError) throw insertError;
        const tx = inserted as CreditTransaction;
        setTransactions((prev) => [tx, ...prev]);

        // Programme les rappels configurés (architecture extensible SMS/WhatsApp)
        const prefs: CreditReminderPrefs = settings?.reminders || DEFAULT_REMINDER_PREFS;
        const shouldRemind =
          (input.type === 'credit' && prefs.new_credit) ||
          (input.type === 'payment' && prefs.payment_confirmation);
        if (shouldRemind) {
          const type = input.type === 'credit' ? 'new_credit' : 'payment_confirmation';
          supabase
            .from('credit_reminders')
            .insert({
              merchant_id: merchantId,
              customer_id: input.customerId,
              type,
              channel: prefs.channel,
              status: 'scheduled',
              scheduled_for: new Date().toISOString(),
              payload: { amount: input.amount, description: input.description || null },
            })
            .then(() => load());
        }

        return tx;
      } catch (err) {
        // Échec réseau en cours de route : on bascule en file hors-ligne.
        if (isNetworkError(err)) return queueOffline();
        throw err;
      }
    },
    [merchantId, settings, load]
  );

  const addCredit = useCallback(
    (customerId: number, amount: number, description?: string, paymentMethod?: string, proofUrl?: string | null) =>
      addTransaction({ customerId, type: 'credit', amount, description, paymentMethod, proofUrl }),
    [addTransaction]
  );

  const recordPayment = useCallback(
    (customerId: number, amount: number, description?: string, paymentMethod?: string) =>
      addTransaction({ customerId, type: 'payment', amount, description, paymentMethod }),
    [addTransaction]
  );

  // Dépôt de fonds : le client avance de l'argent au commerçant. Ce montant est
  // ensuite déduit automatiquement au fil des prises (crédits) chez ce vendeur.
  const addDeposit = useCallback(
    (customerId: number, amount: number, description?: string, paymentMethod?: string) =>
      addTransaction({ customerId, type: 'deposit', amount, description, paymentMethod }),
    [addTransaction]
  );

  const addAdjustment = useCallback(
    (customerId: number, amount: number, description?: string, reversesTransactionId?: number | null) =>
      addTransaction({ customerId, type: 'adjustment', amount, description, reversesTransactionId }),
    [addTransaction]
  );

  // Solder la totalité de la dette d'un client (bouton « Tout est payé »).
  // Enregistre un paiement égal au solde restant dû : la dette repasse à zéro.
  const settleDebt = useCallback(
    async (customerId: number, paymentMethod?: string, description?: string) => {
      const bal = computeBalance(byCustomer[customerId] || []);
      if (bal <= 0) return null;
      return addTransaction({
        customerId,
        type: 'payment',
        amount: bal,
        description: description || 'Dette soldée',
        paymentMethod,
      });
    },
    [byCustomer, addTransaction]
  );

  // ── Réglages ───────────────────────────────────────────────
  const ensureSettings = useCallback(async (): Promise<CreditSettings> => {
    if (!merchantId) throw new Error('Non connecté');
    if (settings?.id) return settings;
    const { data: inserted, error: insertError } = await supabase
      .from('credit_settings')
      .insert({
        merchant_id: merchantId,
        currency: user?.monaie || 'MAD',
        reminders: DEFAULT_REMINDER_PREFS,
      })
      .select('*')
      .single();
    if (insertError) throw insertError;
    const fresh = inserted as CreditSettings;
    setSettings(fresh);
    return fresh;
  }, [merchantId, settings, user]);

  const saveSettings = useCallback(
    async (patch: Partial<CreditSettings>) => {
      const current = settings?.id ? settings : await ensureSettings();
      const { error: updError } = await supabase
        .from('credit_settings')
        .update({
          currency: patch.currency ?? current.currency,
          reminders: patch.reminders ?? current.reminders,
          payment_due_day: patch.payment_due_day ?? current.payment_due_day ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', current.id);
      if (updError) throw updError;
      const merged = { ...current, ...patch } as CreditSettings;
      setSettings(merged);
    },
    [settings, ensureSettings]
  );

  // ── Invitations ────────────────────────────────────────────
  const inviteCustomer = useCallback(
    async (customerId: number): Promise<string> => {
      if (!merchantId) throw new Error('Non connecté');
      const token = generateToken();
      const { error: invError } = await supabase.from('credit_invitations').insert({
        merchant_id: merchantId,
        customer_id: customerId,
        token,
        status: 'pending',
      });
      if (invError) throw invError;
      return token;
    },
    [merchantId]
  );

  // ── Imports de dettes (photo) ──────────────────────────────
  const createImportBatch = useCallback(
    async (input: {
      photoCount: number;
      items: { name: string; amount: number; description?: string }[];
      createdCustomerIds: number[];
      createdTransactionIds: number[];
    }): Promise<CreditImportBatch> => {
      if (!merchantId) throw new Error('Non connecté');
      const total = input.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
      const { data: inserted, error: insErr } = await supabase
        .from('credit_import_batches')
        .insert({
          merchant_id: merchantId,
          source: 'photo',
          photo_count: input.photoCount,
          item_count: input.items.length,
          total_amount: total,
          items: input.items,
          created_customer_ids: input.createdCustomerIds,
          created_transaction_ids: input.createdTransactionIds,
          status: 'active',
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      const batch = inserted as CreditImportBatch;
      setImportBatches((prev) => [batch, ...prev]);
      return batch;
    },
    [merchantId]
  );

  // Annule un import : supprime les transactions créées et les clients créés
  // par ce lot (les clients déjà existants, seulement crédités, sont conservés).
  const revertImportBatch = useCallback(
    async (batchId: number) => {
      const batch = importBatches.find((b) => b.id === batchId);
      if (!batch) return;
      const txIds = batch.created_transaction_ids || [];
      const customerIds = batch.created_customer_ids || [];

      if (txIds.length > 0) {
        const { error: txErr } = await supabase.from('credit_transactions').delete().in('id', txIds);
        if (txErr) throw txErr;
      }
      if (customerIds.length > 0) {
        // Supprime toute dette résiduelle des clients créés par ce lot, puis les clients.
        await supabase.from('credit_transactions').delete().in('customer_id', customerIds);
        const { error: cErr } = await supabase.from('credit_customers').delete().in('id', customerIds);
        if (cErr) throw cErr;
      }

      const revertedAt = new Date().toISOString();
      const { error: upErr } = await supabase
        .from('credit_import_batches')
        .update({ status: 'reverted', reverted_at: revertedAt })
        .eq('id', batchId);
      if (upErr) throw upErr;

      setImportBatches((prev) =>
        prev.map((b) => (b.id === batchId ? { ...b, status: 'reverted', reverted_at: revertedAt } : b))
      );
      setTransactions((prev) =>
        prev.filter((tx) => !txIds.includes(tx.id) && !customerIds.includes(tx.customer_id))
      );
      setCustomers((prev) => prev.filter((c) => !customerIds.includes(c.id)));
    },
    [importBatches]
  );

  return {
    merchantId,
    currency,
    customers,
    transactions,
    settings,
    reminders,
    importBatches,
    loading,
    error,
    reload: load,
    reloadSilent: () => load(true),
    stats,
    balances,
    balanceFor,
    transactionsFor,
    byCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    addCredit,
    recordPayment,
    addDeposit,
    addAdjustment,
    settleDebt,
    ensureSettings,
    saveSettings,
    inviteCustomer,
    regenerateCredentials,
    createImportBatch,
    revertImportBatch,
  };
}